import Button, { ButtonVariant } from "@shared/components/ui/Button";
import api from "@shared/services/api";
import { useState } from "react";
import toast from "react-hot-toast";
import { useAuthStore } from "../features/auth/store/authStore";

interface FeedbackPanelProps {
  setModalOpen: (open: boolean) => void;
}

function FeedbackPanel({ setModalOpen }: FeedbackPanelProps) {
  const { user } = useAuthStore();

  const [rating, setRating] = useState<number | null>(3); // Default to middle rating
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!rating) {
      toast.error("Please select a rating");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post("/feedback", {
        rating,
        feedback,
        userId: user?.uid || "anonymous",
        userEmail: user?.email || "anonymous",
      });

      toast.success("Thank you for your feedback!");
      setModalOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold mb-4">
          Give Feedback
        </h2>
        <div className="text-sm">
          Our product depends on customer feedback to improve the overall
          experience!
        </div>
      </div>

      {/* Rate */}
      <section>
        <h3 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-6">
          How likely would you recommend us to a friend or colleague?
        </h3>
        <div className="w-full max-w-xl">
          <div className="relative">
            <div
              className="absolute left-0 top-1/2 -mt-px w-full h-0.5 bg-gray-200 dark:bg-gray-700/60"
              aria-hidden="true"
            ></div>
            <ul className="relative flex justify-between w-full">
              {[1, 2, 3, 4, 5].map((value) => (
                <li key={value} className="flex">
                  <button
                    className={`w-3 h-3 rounded-full ${
                      rating === value
                        ? `bg-violet-500 border-2 border-violet-500`
                        : `bg-white dark:bg-gray-800 border-2 border-gray-400 dark:border-gray-500`
                    }`}
                    onClick={() => setRating(value)}
                  >
                    <span className="sr-only">{value}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="w-full flex justify-between text-sm text-gray-500 dark:text-gray-400 italic mt-3">
            <div>Not at all</div>
            <div>Extremely likely</div>
          </div>
        </div>
      </section>

      {/* Tell us in words */}
      <section>
        <h3 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-5">
          Tell us in words
        </h3>
        {/* Form */}
        <label className="sr-only" htmlFor="feedback">
          Leave a feedback
        </label>
        <textarea
          id="feedback"
          className="form-textarea w-full focus:border-gray-300"
          rows={4}
          placeholder="I really enjoy…"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        ></textarea>
      </section>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-end space-x-2">
        <Button
          onClick={() => setModalOpen(false)}
          variant={ButtonVariant.TERTIARY}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !rating || !feedback}
        >
          {isSubmitting ? "Submitting..." : "Submit Feedback"}
        </Button>
      </div>
    </div>
  );
}

export default FeedbackPanel;
